'use client'

import * as React from 'react'
import {
  ArrowDown,
  ArrowUp,
  CalendarCheck,
  Check,
  Copy,
  Dices,
  Download,
  MoreHorizontal,
  PencilLine,
  RotateCcw,
  Trophy,
} from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  buildRoundOf32Matches,
  createDefaultStandings,
  GROUP_LETTERS,
  type GroupLetter,
  type GroupStandings,
  type MatchId,
  type QualifiedTeam,
  type Team,
} from '@/lib/world-cup-2026/bracket'
import {
  CURRENT_TOURNAMENT_STATE,
  createCurrentTournamentStandings,
  getCurrentTournamentThirdGroups,
  isCurrentTournamentGroupComplete,
} from '@/lib/world-cup-2026/current-state'
import {
  encodePrediction,
  loadPrediction,
  restoreStandings,
  savePrediction,
  serializeOrders,
  type MatchSide,
  type PredictionMode,
  type SavedPrediction,
} from '@/lib/world-cup-2026/prediction-storage'

type RoundKey =
  | 'roundOf32'
  | 'roundOf16'
  | 'quarterFinals'
  | 'semiFinals'
  | 'final'

type KnockoutSide = {
  teamId?: string
  name: string
  seed: string
  pending?: boolean
}

type KnockoutMatch = {
  id: MatchId
  label: string
  round: RoundKey
  home: KnockoutSide
  away: KnockoutSide
}

type SourceMatchTemplate = {
  id: MatchId
  label: string
  round: RoundKey
  homeSource: MatchId
  awaySource: MatchId
}

type TeamNameFormatter = (team: Team) => string

const EXPORT_DIMENSION_EPSILON = 2

const TEAM_FLAG_CODES: Record<string, string> = {
  mexico: 'mx',
  'south-africa': 'za',
  'korea-republic': 'kr',
  czechia: 'cz',
  canada: 'ca',
  'bosnia-and-herzegovina': 'ba',
  qatar: 'qa',
  switzerland: 'ch',
  brazil: 'br',
  morocco: 'ma',
  haiti: 'ht',
  scotland: 'gb-sct',
  usa: 'us',
  paraguay: 'py',
  australia: 'au',
  turkiye: 'tr',
  germany: 'de',
  curacao: 'cw',
  'cote-divoire': 'ci',
  ecuador: 'ec',
  netherlands: 'nl',
  japan: 'jp',
  sweden: 'se',
  tunisia: 'tn',
  belgium: 'be',
  egypt: 'eg',
  'ir-iran': 'ir',
  'new-zealand': 'nz',
  spain: 'es',
  'cabo-verde': 'cv',
  'saudi-arabia': 'sa',
  uruguay: 'uy',
  france: 'fr',
  senegal: 'sn',
  iraq: 'iq',
  norway: 'no',
  argentina: 'ar',
  algeria: 'dz',
  austria: 'at',
  jordan: 'jo',
  portugal: 'pt',
  'dr-congo': 'cd',
  uzbekistan: 'uz',
  colombia: 'co',
  england: 'gb-eng',
  croatia: 'hr',
  ghana: 'gh',
  panama: 'pa',
}

function TeamFlag({
  teamId,
  name,
  className,
}: {
  teamId: string
  name: string
  className?: string
}) {
  const code = TEAM_FLAG_CODES[teamId]
  if (!code) return null
  return (
    <img
      src={`https://flagcdn.com/${code}.svg`}
      alt={name}
      crossOrigin="anonymous"
      className={cn(
        'shrink-0 rounded-[2px] border border-black/10 bg-muted/20 object-cover shadow-sm',
        className,
      )}
      loading="lazy"
    />
  )
}

const INITIAL_THIRD_GROUPS: GroupLetter[] = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
]

const SOURCE_ROUND_TEMPLATES: SourceMatchTemplate[] = [
  {
    id: 'M89',
    label: 'M89',
    round: 'roundOf16',
    homeSource: 'M74',
    awaySource: 'M77',
  },
  {
    id: 'M90',
    label: 'M90',
    round: 'roundOf16',
    homeSource: 'M73',
    awaySource: 'M75',
  },
  {
    id: 'M91',
    label: 'M91',
    round: 'roundOf16',
    homeSource: 'M76',
    awaySource: 'M78',
  },
  {
    id: 'M92',
    label: 'M92',
    round: 'roundOf16',
    homeSource: 'M79',
    awaySource: 'M80',
  },
  {
    id: 'M93',
    label: 'M93',
    round: 'roundOf16',
    homeSource: 'M83',
    awaySource: 'M84',
  },
  {
    id: 'M94',
    label: 'M94',
    round: 'roundOf16',
    homeSource: 'M81',
    awaySource: 'M82',
  },
  {
    id: 'M95',
    label: 'M95',
    round: 'roundOf16',
    homeSource: 'M86',
    awaySource: 'M88',
  },
  {
    id: 'M96',
    label: 'M96',
    round: 'roundOf16',
    homeSource: 'M85',
    awaySource: 'M87',
  },
  {
    id: 'M97',
    label: 'M97',
    round: 'quarterFinals',
    homeSource: 'M89',
    awaySource: 'M90',
  },
  {
    id: 'M98',
    label: 'M98',
    round: 'quarterFinals',
    homeSource: 'M93',
    awaySource: 'M94',
  },
  {
    id: 'M99',
    label: 'M99',
    round: 'quarterFinals',
    homeSource: 'M91',
    awaySource: 'M92',
  },
  {
    id: 'M100',
    label: 'M100',
    round: 'quarterFinals',
    homeSource: 'M95',
    awaySource: 'M96',
  },
  {
    id: 'M101',
    label: 'M101',
    round: 'semiFinals',
    homeSource: 'M97',
    awaySource: 'M98',
  },
  {
    id: 'M102',
    label: 'M102',
    round: 'semiFinals',
    homeSource: 'M99',
    awaySource: 'M100',
  },
  {
    id: 'M104',
    label: 'M104 Final',
    round: 'final',
    homeSource: 'M101',
    awaySource: 'M102',
  },
]

const MATCH_DEPENDENTS = SOURCE_ROUND_TEMPLATES.reduce(
  (acc, template) => {
    acc[template.homeSource] = [
      ...(acc[template.homeSource] ?? []),
      template.id,
    ]
    acc[template.awaySource] = [
      ...(acc[template.awaySource] ?? []),
      template.id,
    ]
    return acc
  },
  {} as Partial<Record<MatchId, MatchId[]>>,
)

export function WorldCup2026Predictor() {
  const t = useTranslations('ToolsWorldCup2026.predictor')
  const tTeam = useTranslations('ToolsWorldCup2026.teams')
  const getTeamName = React.useCallback<TeamNameFormatter>(
    (team) => tTeam(team.id),
    [tTeam],
  )
  const [standings, setStandings] = React.useState<GroupStandings>(() =>
    createCurrentTournamentStandings(),
  )
  const [selectedThirdGroups, setSelectedThirdGroups] = React.useState<
    GroupLetter[]
  >(() => getCurrentTournamentThirdGroups())
  const [predictionMode, setPredictionMode] =
    React.useState<PredictionMode>('current')
  const [winnerSelections, setWinnerSelections] = React.useState<
    Partial<Record<MatchId, MatchSide>>
  >({})
  const [statusMessage, setStatusMessage] = React.useState('')
  const [isShareLinkCopied, setIsShareLinkCopied] = React.useState(false)
  const [isExporting, setIsExporting] = React.useState(false)
  const [hasRestoredPrediction, setHasRestoredPrediction] =
    React.useState(false)
  const [storageError, setStorageError] = React.useState(false)
  const exportRef = React.useRef<HTMLDivElement>(null)
  const shareFeedbackTimerRef = React.useRef<number | null>(null)
  const savedPrediction = React.useMemo<SavedPrediction>(
    () => ({
      v: 2,
      orders: serializeOrders(standings),
      thirds: selectedThirdGroups,
      mode: predictionMode,
      winners: winnerSelections,
    }),
    [standings, selectedThirdGroups, predictionMode, winnerSelections],
  )

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const decoded = loadPrediction(params.get('p'), () => window.localStorage)
    if (decoded) {
      setStandings(restoreStandings(decoded))
      setSelectedThirdGroups(decoded.thirds)
      setPredictionMode(decoded.mode)
      setWinnerSelections(decoded.winners)
      if (params.has('p')) {
        // 导入后移除旧快照，刷新时恢复后续编辑，而不是反复导入原链接。
        const url = new URL(window.location.href)
        url.searchParams.delete('p')
        window.history.replaceState(window.history.state, '', url)
      }
    }
    setHasRestoredPrediction(true)
  }, [])

  React.useEffect(() => {
    return () => {
      if (shareFeedbackTimerRef.current !== null) {
        window.clearTimeout(shareFeedbackTimerRef.current)
      }
    }
  }, [])

  React.useEffect(() => {
    // 等待恢复完成，避免首次渲染的默认值覆盖已有预测。
    if (!hasRestoredPrediction) return
    setStorageError(!savePrediction(savedPrediction, () => window.localStorage))
  }, [hasRestoredPrediction, savedPrediction])

  function clearShareFeedback() {
    if (shareFeedbackTimerRef.current !== null) {
      window.clearTimeout(shareFeedbackTimerRef.current)
      shareFeedbackTimerRef.current = null
    }
    setIsShareLinkCopied(false)
  }

  function showShareFeedback() {
    if (shareFeedbackTimerRef.current !== null) {
      window.clearTimeout(shareFeedbackTimerRef.current)
    }

    setIsShareLinkCopied(true)
    shareFeedbackTimerRef.current = window.setTimeout(() => {
      setIsShareLinkCopied(false)
      shareFeedbackTimerRef.current = null
    }, 2200)
  }

  const roundOf32 = React.useMemo(
    () =>
      selectedThirdGroups.length === 8
        ? buildRoundOf32Matches(standings, selectedThirdGroups)
        : [],
    [selectedThirdGroups, standings],
  )

  const knockoutRounds = React.useMemo(
    () =>
      buildKnockoutRounds(
        roundOf32,
        winnerSelections,
        (matchId) => t('knockout.pendingWinner', { matchId }),
        getTeamName,
      ),
    [getTeamName, roundOf32, t, winnerSelections],
  )

  const champion = React.useMemo(() => {
    const final = knockoutRounds.flat().find((match) => match.id === 'M104')
    const selection = winnerSelections.M104
    if (!final || !selection) return null
    return final[selection]
  }, [knockoutRounds, winnerSelections])

  const modeDescription =
    predictionMode === 'current'
      ? t('mode.currentDescription', {
          date: CURRENT_TOURNAMENT_STATE.lastUpdated,
        })
      : t('mode.customDescription')

  function loadCurrentTournamentMode(status = t('status.currentModeLoaded')) {
    clearShareFeedback()
    setStandings(createCurrentTournamentStandings())
    setSelectedThirdGroups(getCurrentTournamentThirdGroups())
    setWinnerSelections({})
    setPredictionMode('current')
    setStatusMessage(status)
  }

  function startCustomPrediction(status = t('status.customModeLoaded')) {
    clearShareFeedback()
    setStandings(createDefaultStandings())
    setSelectedThirdGroups(INITIAL_THIRD_GROUPS)
    setWinnerSelections({})
    setPredictionMode('custom')
    setStatusMessage(status)
  }

  function moveTeam(group: GroupLetter, index: number, direction: -1 | 1) {
    clearShareFeedback()
    setPredictionMode('custom')
    setStandings((current) => {
      const nextIndex = index + direction
      if (nextIndex < 0 || nextIndex >= current[group].length) return current

      const groupTeams = [...current[group]]
      const [team] = groupTeams.splice(index, 1)
      groupTeams.splice(nextIndex, 0, team)
      return {
        ...current,
        [group]: groupTeams,
      }
    })
    setWinnerSelections({})
    setStatusMessage(t('status.groupReset'))
  }

  function toggleThirdGroup(group: GroupLetter) {
    clearShareFeedback()
    setSelectedThirdGroups((current) => {
      if (current.includes(group)) {
        setPredictionMode('custom')
        return current.filter((item) => item !== group)
      }

      if (current.length >= 8) {
        setStatusMessage(t('status.thirdLimit'))
        return current
      }

      setPredictionMode('custom')
      return [...current, group].sort()
    })
    setWinnerSelections({})
  }

  function selectWinner(match: KnockoutMatch, side: MatchSide) {
    if (match[side].pending) return

    setWinnerSelections((current) => {
      const next = { ...current, [match.id]: side }
      for (const dependent of getDependentMatches(match.id)) {
        delete next[dependent]
      }
      return next
    })
  }

  function resetPrediction() {
    if (predictionMode === 'current') {
      loadCurrentTournamentMode(t('status.reset'))
    } else {
      startCustomPrediction(t('status.reset'))
    }
  }

  function randomPrediction() {
    clearShareFeedback()
    if (selectedThirdGroups.length !== 8) {
      setStatusMessage(t('status.needEightForRandom'))
      return
    }

    const nextSelections: Partial<Record<MatchId, MatchSide>> = {}

    for (const match of roundOf32) {
      nextSelections[match.id] = Math.random() > 0.5 ? 'home' : 'away'
    }

    for (const template of SOURCE_ROUND_TEMPLATES) {
      nextSelections[template.id] = Math.random() > 0.5 ? 'home' : 'away'
    }

    setWinnerSelections(nextSelections)
    setStatusMessage(t('status.randomCompleted'))
  }

  async function copyShareLink() {
    const encoded = encodePrediction(savedPrediction)
    const url = new URL(window.location.href)
    url.searchParams.set('p', encoded)

    try {
      await navigator.clipboard.writeText(url.toString())
      setStatusMessage('')
      showShareFeedback()
    } catch (error) {
      console.error(error)
      clearShareFeedback()
      setStatusMessage(t('status.copyFailed'))
    }
  }

  async function exportPng() {
    if (!exportRef.current || isExporting) return

    clearShareFeedback()
    setIsExporting(true)
    try {
      const node = exportRef.current
      const rect = node.getBoundingClientRect()
      const exportWidth = Math.ceil(
        Math.max(node.scrollWidth, rect.width) + EXPORT_DIMENSION_EPSILON,
      )
      const exportHeight = Math.ceil(
        Math.max(node.scrollHeight, rect.height) + EXPORT_DIMENSION_EPSILON,
      )

      const { toPng } = await import('html-to-image')
      const dataUrl = await toPng(node, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        width: exportWidth,
        height: exportHeight,
        style: {
          width: `${exportWidth}px`,
          height: `${exportHeight}px`,
        },
      })
      const link = document.createElement('a')
      link.download = 'world-cup-2026-bracket-predictor.png'
      link.href = dataUrl
      link.click()
      setStatusMessage(t('status.pngExported'))
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <section
      id="world-cup-2026-predictor"
      className="mx-auto max-w-7xl space-y-12"
    >
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="grid lg:grid-cols-12">
          <div className="space-y-4 p-6 md:p-8 lg:col-span-8">
            <div className="flex flex-wrap gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <span className="rounded-full border border-border bg-background px-2.5 py-0.5">
                {t('badges.teams')}
              </span>
              <span className="rounded-full border border-border bg-background px-2.5 py-0.5">
                {t('badges.groups')}
              </span>
              <span className="rounded-full border border-border bg-background px-2.5 py-0.5">
                {t('badges.annex')}
              </span>
            </div>
            <h2 className="text-3xl font-bold tracking-tight">{t('title')}</h2>
            <p className="max-w-2xl text-base text-muted-foreground">
              {t('intro')}
            </p>
          </div>

          <div className="border-t border-border bg-muted/20 p-6 lg:col-span-4 lg:border-l lg:border-t-0 lg:p-8">
            <div className="space-y-4">
              <div className="space-y-2.5">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('mode.label')}
                </div>
                <div
                  role="group"
                  aria-label={t('mode.label')}
                  className="grid rounded-md border border-border bg-background p-1 sm:grid-cols-2"
                >
                  <button
                    type="button"
                    aria-pressed={predictionMode === 'current'}
                    onClick={() => loadCurrentTournamentMode()}
                    className={cn(
                      'inline-flex h-9 items-center justify-center gap-1.5 rounded-sm px-3 text-xs font-semibold transition-colors',
                      predictionMode === 'current'
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    <CalendarCheck className="h-3.5 w-3.5" />
                    {t('mode.current')}
                  </button>
                  <button
                    type="button"
                    aria-pressed={predictionMode === 'custom'}
                    onClick={() => startCustomPrediction()}
                    className={cn(
                      'inline-flex h-9 items-center justify-center gap-1.5 rounded-sm px-3 text-xs font-semibold transition-colors',
                      predictionMode === 'custom'
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    <PencilLine className="h-3.5 w-3.5" />
                    {t('mode.custom')}
                  </button>
                </div>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {modeDescription}
                </p>
              </div>

              <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-2">
                <Button
                  size="sm"
                  onClick={exportPng}
                  disabled={isExporting}
                  className="justify-center"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {isExporting
                    ? t('actions.exporting')
                    : t('actions.exportPng')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyShareLink}
                  title={t('actions.copyLink')}
                  className={cn(
                    'px-3 transition-all sm:px-4',
                    isShareLinkCopied &&
                      'border-emerald-500/70 bg-emerald-500/10 text-emerald-700 shadow-[0_0_0_3px_rgba(16,185,129,0.16)] hover:bg-emerald-500/15 hover:text-emerald-800 dark:border-emerald-400/60 dark:text-emerald-300 dark:hover:text-emerald-200',
                  )}
                >
                  {isShareLinkCopied ? (
                    <Check className="h-4 w-4 sm:mr-2" />
                  ) : (
                    <Copy className="h-4 w-4 sm:mr-2" />
                  )}
                  <span className="hidden sm:inline">
                    {isShareLinkCopied
                      ? t('actions.copied')
                      : t('actions.copyLink')}
                  </span>
                </Button>
                <details className="group relative">
                  <summary
                    aria-label={t('actions.more')}
                    title={t('actions.more')}
                    className="inline-flex h-8 cursor-pointer list-none items-center justify-center rounded-md border border-input bg-background px-3 text-xs font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring [&::-webkit-details-marker]:hidden"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </summary>
                  <div className="absolute right-0 top-full z-20 mt-2 w-44 rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
                    <button
                      type="button"
                      onClick={(event) => {
                        closeDetailsMenu(event)
                        randomPrediction()
                      }}
                      disabled={selectedThirdGroups.length !== 8}
                      className="flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
                    >
                      <Dices className="mr-2 h-4 w-4" />
                      {t('actions.random')}
                    </button>
                    <button
                      type="button"
                      onClick={(event) => {
                        closeDetailsMenu(event)
                        resetPrediction()
                      }}
                      className="flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      {t('actions.reset')}
                    </button>
                  </div>
                </details>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-px bg-border sm:grid-cols-3 lg:grid-cols-12">
          <Metric
            label={t('metrics.thirdPlaces')}
            value={`${selectedThirdGroups.length}/8`}
            className="lg:col-span-4"
          />
          <Metric
            label={t('metrics.knockoutMatches')}
            value={
              selectedThirdGroups.length === 8 ? '16' : t('metrics.waiting')
            }
            className="lg:col-span-4"
          />
          <Metric
            label={t('metrics.championPick')}
            value={champion?.name ?? t('metrics.notSelected')}
            icon={
              champion?.teamId && (
                <TeamFlag
                  teamId={champion.teamId}
                  name={champion.name}
                  className="h-[22px] w-[30px]"
                />
              )
            }
            strong={Boolean(champion)}
            className="lg:col-span-4"
          />
        </div>
      </div>

      {storageError ? (
        <div
          role="status"
          className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground"
        >
          {t('status.storageUnavailable')}
        </div>
      ) : null}

      {statusMessage ? (
        <div
          role="status"
          aria-live="polite"
          className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground"
        >
          {statusMessage}
        </div>
      ) : null}

      <div
        ref={exportRef}
        className="space-y-12 overflow-visible bg-background p-6 pb-14 text-foreground sm:p-8 sm:pb-16"
      >
        <section className="space-y-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold tracking-tight">
                {t('groupStage.title')}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('groupStage.intro')}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedThirdGroups.map((group) => (
                <span
                  key={group}
                  className="rounded bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary"
                >
                  3{group}
                </span>
              ))}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {GROUP_LETTERS.map((group) => (
              <GroupPanel
                key={group}
                group={group}
                teams={standings[group]}
                selectedThird={selectedThirdGroups.includes(group)}
                finalGroup={
                  predictionMode === 'custom' ||
                  isCurrentTournamentGroupComplete(group)
                }
                onMove={moveTeam}
                onToggleThird={toggleThirdGroup}
                getTeamName={getTeamName}
                copy={{
                  groupLabel: t('groupStage.groupLabel', { group }),
                  teamCount: t('groupStage.teamCount'),
                  qualified: t('groupStage.qualified'),
                  projected: t('groupStage.projected'),
                  bestThird: t('groupStage.bestThird'),
                  projectedThird: t('groupStage.projectedThird'),
                  moveUp: (team) => t('groupStage.moveUp', { team }),
                  moveDown: (team) => t('groupStage.moveDown', { team }),
                }}
              />
            ))}
          </div>
        </section>

        <section className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold tracking-tight">
                {t('knockout.title')}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('knockout.intro')}
              </p>
            </div>
            {champion ? (
              <div className="group relative flex items-center gap-3.5 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-transparent py-2.5 pl-3 pr-6 shadow-[0_0_20px_rgba(245,158,11,0.1)] transition-all hover:border-amber-500/50 hover:shadow-[0_0_25px_rgba(245,158,11,0.2)]">
                <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-yellow-300 via-amber-500 to-yellow-600 shadow-lg ring-4 ring-amber-500/20">
                  <Trophy className="h-6 w-6 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]" />
                  <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-white/0 via-white/40 to-white/0 opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-black uppercase tracking-widest text-amber-600 dark:text-amber-400">
                    {t('metrics.championPick')}
                  </span>
                  <div className="mt-0.5 flex items-center gap-2">
                    {champion.teamId && (
                      <TeamFlag
                        teamId={champion.teamId}
                        name={champion.name}
                        className="h-4 w-6 rounded-sm shadow-sm"
                      />
                    )}
                    <span className="text-xl font-black tracking-tight text-foreground">
                      {champion.name}
                    </span>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {selectedThirdGroups.length !== 8 ? (
            <div className="rounded-lg border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
              {t('knockout.needEight')}
            </div>
          ) : (
            <div className="grid gap-4 xl:grid-cols-5">
              {knockoutRounds.map((round) => (
                <div key={round[0]?.round} className="space-y-4">
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    {round[0]?.round ? t(`rounds.${round[0].round}`) : ''}
                  </h4>
                  <div className="space-y-3">
                    {round.map((match) => (
                      <KnockoutMatchCard
                        key={match.id}
                        match={match}
                        selectedSide={winnerSelections[match.id]}
                        onSelect={selectWinner}
                        vsLabel={t('knockout.vs')}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </section>
  )
}

function Metric({
  label,
  value,
  icon,
  strong = false,
  className,
}: {
  label: string
  value: string
  icon?: React.ReactNode
  strong?: boolean
  className?: string
}) {
  return (
    <div className={cn('bg-card p-6', className)}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          'mt-2 flex items-center gap-2.5 text-2xl font-bold tracking-tight',
          strong ? 'text-primary' : 'text-foreground',
        )}
      >
        {icon}
        {value}
      </div>
    </div>
  )
}

function GroupPanel({
  group,
  teams,
  selectedThird,
  finalGroup,
  onMove,
  onToggleThird,
  getTeamName,
  copy,
}: {
  group: GroupLetter
  teams: GroupStandings[GroupLetter]
  selectedThird: boolean
  finalGroup: boolean
  onMove: (group: GroupLetter, index: number, direction: -1 | 1) => void
  onToggleThird: (group: GroupLetter) => void
  getTeamName: TeamNameFormatter
  copy: {
    groupLabel: string
    teamCount: string
    qualified: string
    projected: string
    bestThird: string
    projectedThird: string
    moveUp: (team: string) => string
    moveDown: (team: string) => string
  }
}) {
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <h4 className="text-base font-bold">{copy.groupLabel}</h4>
        <span className="text-xs font-semibold text-muted-foreground">
          {copy.teamCount}
        </span>
      </div>

      <div className="divide-y divide-border">
        {teams.map((team, index) => {
          const rank = index + 1
          const isThird = rank === 3
          const qualifies = rank <= 2 || (isThird && selectedThird)
          const teamName = getTeamName(team)
          return (
            <div
              key={team.id}
              className={cn(
                'grid grid-cols-[auto_1fr_auto] items-center gap-4 px-5 py-3 transition-colors',
                qualifies
                  ? 'bg-primary/5 hover:bg-primary/10'
                  : 'bg-transparent hover:bg-muted/50',
              )}
            >
              <span
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded text-sm font-bold',
                  rank <= 2
                    ? 'bg-primary text-primary-foreground'
                    : qualifies
                      ? 'bg-primary/20 text-primary'
                      : 'bg-muted text-muted-foreground',
                )}
              >
                {rank}
              </span>

              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <TeamFlag
                    teamId={team.id}
                    name={teamName}
                    className="h-3 w-[18px]"
                  />
                  <div className="truncate text-sm font-medium">{teamName}</div>
                </div>
                <div className="mt-1 flex flex-wrap gap-2 text-[11px] font-medium">
                  {rank <= 2 ? (
                    <span className="text-primary">
                      {finalGroup ? copy.qualified : copy.projected}
                    </span>
                  ) : null}
                  {isThird ? (
                    <button
                      type="button"
                      aria-pressed={selectedThird}
                      onClick={() => onToggleThird(group)}
                      className={cn(
                        'inline-flex items-center gap-1 transition-colors',
                        selectedThird
                          ? 'text-primary hover:text-primary/80'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {selectedThird ? <Check className="h-3 w-3" /> : null}
                      {finalGroup ? copy.bestThird : copy.projectedThird}
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="flex flex-col gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 rounded-sm text-muted-foreground hover:text-foreground"
                  aria-label={copy.moveUp(teamName)}
                  disabled={index === 0}
                  onClick={() => onMove(group, index, -1)}
                >
                  <ArrowUp className="h-3 w-3" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 rounded-sm text-muted-foreground hover:text-foreground"
                  aria-label={copy.moveDown(teamName)}
                  disabled={index === teams.length - 1}
                  onClick={() => onMove(group, index, 1)}
                >
                  <ArrowDown className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function KnockoutMatchCard({
  match,
  selectedSide,
  onSelect,
  vsLabel,
}: {
  match: KnockoutMatch
  selectedSide?: MatchSide
  onSelect: (match: KnockoutMatch, side: MatchSide) => void
  vsLabel: string
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 transition-all hover:border-primary/30">
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          {match.label}
        </span>
        <span className="text-[11px] font-medium text-muted-foreground">
          {match.home.seed} <span className="opacity-50">{vsLabel}</span>{' '}
          {match.away.seed}
        </span>
      </div>
      <div className="space-y-1.5">
        <TeamAdvanceButton
          side="home"
          team={match.home}
          selected={selectedSide === 'home'}
          onClick={() => onSelect(match, 'home')}
        />
        <TeamAdvanceButton
          side="away"
          team={match.away}
          selected={selectedSide === 'away'}
          onClick={() => onSelect(match, 'away')}
        />
      </div>
    </div>
  )
}

function TeamAdvanceButton({
  team,
  selected,
  onClick,
}: {
  side: MatchSide
  team: KnockoutSide
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={team.pending}
      onClick={onClick}
      className={cn(
        'flex min-h-10 w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm font-medium transition-all',
        selected
          ? 'border-primary bg-primary text-primary-foreground shadow-sm'
          : 'border-border bg-transparent hover:border-primary/50 hover:bg-muted/30',
        team.pending &&
          'cursor-not-allowed border-dashed text-muted-foreground opacity-50 hover:border-border hover:bg-transparent',
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        {team.teamId && !team.pending && (
          <TeamFlag
            teamId={team.teamId}
            name={team.name}
            className="h-3.5 w-[21px]"
          />
        )}
        <span className="truncate">{team.name}</span>
      </div>
      {selected ? <Check className="h-4 w-4 shrink-0" /> : null}
    </button>
  )
}

function buildKnockoutRounds(
  roundOf32: ReturnType<typeof buildRoundOf32Matches>,
  winnerSelections: Partial<Record<MatchId, MatchSide>>,
  pendingWinnerLabel: (matchId: MatchId) => string,
  getTeamName: TeamNameFormatter,
): KnockoutMatch[][] {
  const matchesById = new Map<MatchId, KnockoutMatch>()
  const rounds: KnockoutMatch[][] = [
    roundOf32.map((match) => {
      const knockoutMatch: KnockoutMatch = {
        id: match.id,
        label: match.label,
        round: 'roundOf32',
        home: toKnockoutSide(match.home, getTeamName),
        away: toKnockoutSide(match.away, getTeamName),
      }
      matchesById.set(knockoutMatch.id, knockoutMatch)
      return knockoutMatch
    }),
  ]

  for (const template of SOURCE_ROUND_TEMPLATES) {
    const match: KnockoutMatch = {
      id: template.id,
      label: template.label,
      round: template.round,
      home: getMatchWinnerSide(
        template.homeSource,
        matchesById,
        winnerSelections,
        pendingWinnerLabel,
      ),
      away: getMatchWinnerSide(
        template.awaySource,
        matchesById,
        winnerSelections,
        pendingWinnerLabel,
      ),
    }

    matchesById.set(match.id, match)

    const existingRound = rounds.find(
      (round) => round[0]?.round === match.round,
    )
    if (existingRound) {
      existingRound.push(match)
    } else {
      rounds.push([match])
    }
  }

  return rounds
}

function toKnockoutSide(
  team: QualifiedTeam,
  getTeamName: TeamNameFormatter,
): KnockoutSide {
  return {
    teamId: team.id,
    name: getTeamName(team),
    seed: team.seed,
  }
}

function getMatchWinnerSide(
  source: MatchId,
  matchesById: Map<MatchId, KnockoutMatch>,
  winnerSelections: Partial<Record<MatchId, MatchSide>>,
  pendingWinnerLabel: (matchId: MatchId) => string,
): KnockoutSide {
  const sourceMatch = matchesById.get(source)
  const selection = winnerSelections[source]

  if (!sourceMatch || !selection) {
    return {
      name: pendingWinnerLabel(source),
      seed: source,
      pending: true,
    }
  }

  return sourceMatch[selection]
}

function getDependentMatches(matchId: MatchId) {
  const visited = new Set<MatchId>()
  const queue = [...(MATCH_DEPENDENTS[matchId] ?? [])]

  while (queue.length > 0) {
    const next = queue.shift()
    if (!next || visited.has(next)) continue
    visited.add(next)
    queue.push(...(MATCH_DEPENDENTS[next] ?? []))
  }

  return visited
}

function closeDetailsMenu(event: React.MouseEvent<HTMLButtonElement>) {
  const details = event.currentTarget.closest('details')
  if (details instanceof HTMLDetailsElement) {
    details.open = false
  }
}
